import type { HistoryEventRecord, SummaryRecord, SummaryScope } from '../../domain/types.js';
import { SUMMARY_RECORD_SCHEMA_VERSION } from '../../domain/types.js';

function hashIds(ids: readonly string[]): string {
  return ids.slice().sort().join('|');
}

function buildSummaryText(events: HistoryEventRecord[]): { observed: string; interpretation?: string } {
  if (events.length === 0) {
    return { observed: 'No recorded activity yet.' };
  }
  const lines = events
    .slice()
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
    .map((e) => e.summary);
  return {
    observed: lines.join(' '),
    interpretation: events.length > 1 ? 'Compiled from persisted events in order.' : undefined,
  };
}

export function buildSummaryFromEvents(args: {
  worldId: string;
  scope: SummaryScope;
  events: HistoryEventRecord[];
  characterId?: string;
  prior?: SummaryRecord | null;
}): SummaryRecord | null {
  const { worldId, scope, events, characterId, prior } = args;
  const eventIds = events.map((e) => e.eventId);
  const priorHash = prior ? hashIds(prior.eventIds) : undefined;
  const nextHash = hashIds(eventIds);
  if (prior && priorHash === nextHash) return prior;
  const { observed, interpretation } = buildSummaryText(events);
  return {
    schemaVersion: SUMMARY_RECORD_SCHEMA_VERSION,
    summaryId: `${scope}_${worldId}_${nextHash.slice(0, 24) || 'empty'}`,
    worldId,
    scope,
    characterId,
    eventIds,
    observedFacts: observed,
    directorInterpretation: interpretation,
    priorSummaryHash: priorHash,
    createdAt: new Date().toISOString(),
  };
}

export function filterEventsForCharacter(
  events: HistoryEventRecord[],
  characterId: string,
): HistoryEventRecord[] {
  return events.filter((e) => e.actorIds.includes(characterId));
}
